import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  categoryService,
  subcategoryService,
  productService,
  adminService,
  videoService,
  reportService,
  testService
} from '../services/api';
import {
  Category,
  SubcategoryHierarchy,
  Product,
  CreateSubcategoryData,
  CreateProductData,
  CreateSubcategoryResponse,
  CreateProductResponse,
  Video,
  CreateVideoData,
  UpdateVideoData,
  CreateTestQuestionData,
  UpdateTestQuestionData
} from '../types';
import toast from 'react-hot-toast';
import { getApiError } from '../utils/apiError';

// Hook para obtener categorías
export const useAdminCategories = () => {
  return useQuery({
    queryKey: ['admin-categories'],
    queryFn: categoryService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

// Hook para obtener subcategorías jerárquicas por categoría
export const useAdminSubcategories = (categoryId?: string) => {
  return useQuery({
    queryKey: ['admin-subcategories', categoryId],
    queryFn: () => categoryService.getById(categoryId!),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      return data.category?.subcategories || []
    }
  });
};

// Hook para obtener todas las subcategorías planas con información de categoría - CORREGIDO
export const useAdminAllSubcategories = () => {
  const { data: categoriesData } = useAdminCategories();
  
  return useMemo(() => {
    if (!categoriesData?.categories) {
      return [];
    }
    
    const flatSubcategories: Array<SubcategoryHierarchy & { categoryName: string; categoryId: string; level?: number }> = [];
    
    // Función recursiva mejorada para aplanar subcategorías
    const flattenSubcategories = (
      subcategories: SubcategoryHierarchy[], 
      categoryName: string, 
      categoryId: string,
      level: number = 0
    ) => {
      if (!subcategories || subcategories.length === 0) return;
      
      subcategories.forEach(sub => {
        // Agregar subcategoría actual con información de categoría
        const flatSubcategory = {
          ...sub,
          categoryName,
          categoryId,
          level: sub.level !== undefined ? sub.level : level // Usar level de la DB si existe
        };
        
        flatSubcategories.push(flatSubcategory);
                
        // Procesar hijos recursivamente
        if (sub.children && sub.children.length > 0) {
          flattenSubcategories(sub.children, categoryName, categoryId, level + 1);
        }
      });
    };
    
    
    categoriesData.categories.forEach(category => {
      if (category.subcategories && category.subcategories.length > 0) {
        flattenSubcategories(category.subcategories, category.name, category.id, 0);
      }
    });
    
    return flatSubcategories.sort((a, b) => {
      // Ordenar por categoría, luego por nivel, luego por nombre
      if (a.categoryName !== b.categoryName) {
        return a.categoryName.localeCompare(b.categoryName);
      }
      if (a.level !== b.level) {
        return (a.level || 0) - (b.level || 0);
      }
      return a.name.localeCompare(b.name);
    });
  }, [categoriesData]);
};

// Hook para obtener productos por subcategoría
export const useAdminProducts = (categoryId?: string) => {
  return useQuery<{ products: Product[] }>({
    queryKey: ['admin-products', categoryId || 'all'],
    queryFn: async () => {
      if (!categoryId) {
        // Sin filtro → traer todos los productos
        const response = await productService.getAll();
        return { products: response.products || [] };
      }

      // Con categoría → traer productos de sus subcategorías
      const response = await productService.getByCategory(categoryId);
      return { products: response.products || [] };
    },
    staleTime: 0,
    placeholderData: (prev) => prev, // 👈 reemplazo de keepPreviousData
  });
};

// Hook para obtener todos los productos con información de categoría y subcategoría - CORREGIDO
export const useAdminAllProducts = () => {
  const { data: categoriesData } = useAdminCategories();
  
  return useMemo(() => {
    if (!categoriesData?.categories) {
      return [];
    }
    
    const allProducts: Array<Product & { 
      categoryName: string; 
      categoryId: string; 
      subcategoryName: string;
      subcategoryPath: string;
    }> = [];
    
    const extractProducts = (
      subcategories: SubcategoryHierarchy[], 
      categoryName: string, 
      categoryId: string,
      path: string[] = []
    ) => {
      if (!subcategories || subcategories.length === 0) return;
      
      subcategories.forEach(sub => {
        const currentPath = [...path, sub.name];
        
        // Extraer productos de esta subcategoría
        if (sub.products && sub.products.length > 0) {
          sub.products.forEach(product => {
            const productWithInfo = {
              ...product,
              categoryName,
              categoryId,
              subcategoryName: sub.name,
              subcategoryPath: currentPath.join(' > ')
            };
            allProducts.push(productWithInfo);
          });
        }
        
        // Procesar hijos recursivamente
        if (sub.children && sub.children.length > 0) {
          extractProducts(sub.children, categoryName, categoryId, currentPath);
        }
      });
    };
    
    categoriesData.categories.forEach(category => {
      if (category.subcategories && category.subcategories.length > 0) {
        extractProducts(category.subcategories, category.name, category.id);
      }
    });
    
    return allProducts.sort((a, b) => a.name.localeCompare(b.name));
  }, [categoriesData]);
};

// Hook para crear subcategoría
export const useCreateSubcategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: subcategoryService.create,
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-subcategories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      
      toast.success('Subcategoría creada exitosamente');
    },
    onError: (error: any) => {
      console.error('Error creando subcategoría:', error);
      toast.error(error.response?.data?.error || 'Error creando subcategoría');
    },
  });
};

// Hook para crear producto
export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: productService.create,
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      
      
      toast.success('Producto creado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error creando producto:', error);
      toast.error(error.response?.data?.error || 'Error creando producto');
    },
  });
};

// Hook para eliminar subcategoría (QUIRÚRGICO)
export const useDeleteSubcategory = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    // Espera { id, categoryId }
    mutationFn: ({ id }: { id: string; categoryId?: string }) => subcategoryService.delete(id),
    onSuccess: (_data, vars) => {
      const { id, categoryId } = vars || {};

      // 1) Actualiza lista de subcategorías de esa categoría en cache
      if (categoryId) {
        queryClient.setQueryData<any[]>(
          ['admin-subcategories', categoryId],
          (old) => Array.isArray(old) ? old.filter(s => s.id !== id) : old
        );
        queryClient.invalidateQueries({ queryKey: ['admin-subcategories', categoryId], refetchType: 'active' });
      }

      // 2) Podar el árbol en admin-categories (sin refetch masivo)
      queryClient.setQueryData<{ categories: any[] }>(
        ['admin-categories'],
        (old) => {
          if (!old?.categories) return old;
          const clone = structuredClone(old);
          for (const cat of clone.categories) {
            const prune = (subs: any[]): any[] =>
              (subs || []).filter((s: any) => {
                s.children = prune(s.children);
                return s.id !== id;
              });
            cat.subcategories = prune(cat.subcategories);
          }
          return clone;
        }
      );

      // 3) (Opcional) invalidar vistas agregadas si están activas
      // queryClient.invalidateQueries({ queryKey: ['admin-all-subcategories'], refetchType: 'active' });

      toast.success('Subcategoría eliminada exitosamente');
    },
    onError: (error: any) => {
      console.error('Error eliminando subcategoría:', error);
      toast.error(error.response?.data?.error || 'Error eliminando subcategoría');
    },
  });
};

/// Hook para actualizar producto
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Product> }) =>
      await productService.update(id, data),

    onSuccess: (_res, vars) => {
      // 🧼 Invalidar queries para forzar refetch y refrescar productos e imágenes
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['admin-all-products'] });
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });

      // 🚀 Limpieza manual opcional: borrar objeto específico del caché si existe
      if (vars?.id) {
        queryClient.removeQueries({ queryKey: ['admin-product', vars.id] });
      }

      toast.success('Producto actualizado exitosamente');
    },

    onError: (error: any) => {
      console.error('Error actualizando producto:', error);
      toast.error(error.response?.data?.error || 'Error actualizando producto');
    },
  });
};

// Hook para eliminar producto (QUIRÚRGICO)
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    // Espera { id, subcategoryId }
    mutationFn: ({ id }: { id: string; subcategoryId?: string }) => productService.delete(id),
    onSuccess: (_data, vars) => {
      const { id, subcategoryId } = vars || {};

      // 1) Actualiza lista de productos de esa subcategoría en cache
      if (subcategoryId) {
        queryClient.setQueryData<{ products: any[] }>(
          ['admin-products', subcategoryId],
          (old) => old ? { products: old.products.filter(p => p.id !== id) } : old
        );
        queryClient.invalidateQueries({ queryKey: ['admin-products', subcategoryId], refetchType: 'active' });
      }

      // 2) Actualiza árbol de admin-categories en cache (sin refetch masivo)
      queryClient.setQueryData<{ categories: any[] }>(
        ['admin-categories'],
        (old) => {
          if (!old?.categories) return old;
          const clone = structuredClone(old);
          for (const cat of clone.categories) {
            const walk = (subs: any[]) => {
              subs?.forEach((s) => {
                if (Array.isArray(s.products)) {
                  s.products = s.products.filter((p: any) => p.id !== id);
                }
                if (s.children?.length) walk(s.children);
              });
            };
            walk(cat.subcategories);
          }
          return clone;
        }
      );

      // 3) (Opcional) invalidar vistas agregadas si están activas
      // queryClient.invalidateQueries({ queryKey: ['admin-all-products'], refetchType: 'active' });

      toast.success('Producto eliminado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error eliminando producto:', error);
      toast.error(error.response?.data?.error || 'Error eliminando producto');
    },
  });
};

// Hook para exportar datos
export const useExportData = () => {
  return useMutation({
    mutationFn: ({ type, dateRange }: { type: 'users' | 'activities' | 'certificates', dateRange?: string }) =>
      reportService.exportData(type, dateRange),
    onSuccess: () => {
      toast.success('Datos exportados exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error exportando datos');
    },
  });
};

// Hook para obtener todos los videos (admin)
export const useAdminVideos = () => {
  return useQuery({
    queryKey: ['admin-videos'],
    queryFn: videoService.getAll,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para obtener videos por categoría
export const useAdminVideosByCategory = (categoryId?: string) => {
  return useQuery({
    queryKey: ['admin-videos-category', categoryId],
    queryFn: () => videoService.getByCategory(categoryId!),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para crear video
export const useCreateVideo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: videoService.create,
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });
      queryClient.invalidateQueries({ queryKey: ['admin-videos-category'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      
      toast.success('Video creado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error creando video:', error);
      toast.error(error.response?.data?.error || 'Error creando video');
    },
  });
};

// Hook para actualizar video
export const useUpdateVideo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVideoData }) => 
      videoService.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });
      queryClient.invalidateQueries({ queryKey: ['admin-videos-category'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      
      toast.success('Video actualizado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error actualizando video:', error);
      toast.error(error.response?.data?.error || 'Error actualizando video');
    },
  });
};

// Hook para eliminar video
export const useDeleteVideo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: videoService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });
      queryClient.invalidateQueries({ queryKey: ['admin-videos-category'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      
      toast.success('Video eliminado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error eliminando video:', error);
      toast.error(error.response?.data?.error || 'Error eliminando video');
    },
  });
};

// ─── Hooks de Test Questions ───────────────────────────────────────────────

export const useAdminTestQuestions = (categoryId?: string) => {
  return useQuery({
    queryKey: ['admin-tests', categoryId],
    queryFn: () => testService.getByCategory(categoryId!),
    enabled: !!categoryId,
    staleTime: 2 * 60 * 1000,
    select: (data) => data.questions,
  });
};

export const useCreateTestQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTestQuestionData) => testService.create(data),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tests', vars.categoryId] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Pregunta creada exitosamente');
    },
    onError: (error: unknown) => {
      toast.error(getApiError(error, 'Error creando pregunta'));
    },
  });
};

export const useUpdateTestQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; categoryId: string; data: UpdateTestQuestionData }) =>
      testService.update(id, data),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tests', vars.categoryId] });
      toast.success('Pregunta actualizada exitosamente');
    },
    onError: (error: unknown) => {
      toast.error(getApiError(error, 'Error actualizando pregunta'));
    },
  });
};

export const useDeleteTestQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; categoryId: string }) => testService.delete(id),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tests', vars.categoryId] });
      toast.success('Pregunta eliminada exitosamente');
    },
    onError: (error: unknown) => {
      toast.error(getApiError(error, 'Error eliminando pregunta'));
    },
  });
};

// Hook principal para el componente AdminContent
export const useAdminContent = () => {
  const [activeTab, setActiveTab] = useState<'categories' | 'subcategories' | 'products' | 'videos' | 'tests'>('categories');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('');

  // Queries
  const categoriesQuery = useAdminCategories();
  const subcategoriesQuery = useAdminSubcategories(selectedCategoryId);
  const allSubcategoriesQuery = useAdminAllSubcategories();
  const productsQuery = useAdminProducts(selectedSubcategoryId);
  const allProductsQuery = useAdminAllProducts();
  const videosQuery = useAdminVideos();
  const testQuestionsQuery = useAdminTestQuestions(selectedCategoryId || undefined);

  // Mutations de test questions
  const createTestQuestion = useCreateTestQuestion();
  const updateTestQuestion = useUpdateTestQuestion();
  const deleteTestQuestion = useDeleteTestQuestion();

  // Mutations
  const createSubcategory = useCreateSubcategory();
  const createProduct = useCreateProduct();
  const deleteSubcategory = useDeleteSubcategory();
  const deleteProduct = useDeleteProduct();
  const createVideo = useCreateVideo();
  const updateVideo = useUpdateVideo();
  const deleteVideo = useDeleteVideo();

  const updateProduct = useUpdateProduct();

  // Auto-seleccionar primera categoría cuando se cargan
  useEffect(() => {
    if (categoriesQuery.data?.categories?.length && !selectedCategoryId) {
      const firstCategoryId = categoriesQuery.data.categories[0].id;
      setSelectedCategoryId(firstCategoryId);
    }
  }, [categoriesQuery.data, selectedCategoryId]);


  // Funciones de utilidad
  const getCategoryStats = useCallback((categoryId: string) => {
    const category = categoriesQuery.data?.categories?.find(c => c.id === categoryId);
    if (!category?.subcategories) return { subcategories: 0, products: 0 };

    const countStats = (subcategories: SubcategoryHierarchy[]): { subcategories: number; products: number } => {
      return subcategories.reduce((acc, sub) => {
        let subcategoryCount = 1;
        let productCount = sub.products?.length || 0;

        if (sub.children && sub.children.length > 0) {
          const childStats = countStats(sub.children);
          subcategoryCount += childStats.subcategories;
          productCount += childStats.products;
        }

        return {
          subcategories: acc.subcategories + subcategoryCount,
          products: acc.products + productCount
        };
      }, { subcategories: 0, products: 0 });
    };

    return countStats(category.subcategories);
  }, [categoriesQuery.data]);

  return {
    // Estado
    activeTab,
    setActiveTab,
    selectedCategoryId,
    setSelectedCategoryId,
    selectedSubcategoryId,
    setSelectedSubcategoryId,

    // Datos
    categories: categoriesQuery.data?.categories || [],
    subcategories: subcategoriesQuery.data || [],
    allSubcategories: allSubcategoriesQuery,
    products: productsQuery.data?.products || [],
    allProducts: allProductsQuery,
    videos: videosQuery.data?.videos || [],
    isLoadingVideos: videosQuery.isLoading,
    testQuestions: testQuestionsQuery.data || [],
    isLoadingTests: testQuestionsQuery.isLoading,

    // Estados de carga
    isLoadingCategories: categoriesQuery.isLoading,
    isLoadingSubcategories: subcategoriesQuery.isLoading,
    isLoadingProducts: productsQuery.isLoading,

    // Mutations de test questions
    createTestQuestion: createTestQuestion.mutateAsync,
    updateTestQuestion: updateTestQuestion.mutateAsync,
    deleteTestQuestion: deleteTestQuestion.mutateAsync,
    isCreatingTestQuestion: createTestQuestion.isPending,
    isUpdatingTestQuestion: updateTestQuestion.isPending,
    isDeletingTestQuestion: deleteTestQuestion.isPending,

    // Mutations
    createSubcategory: createSubcategory.mutateAsync,
    createProduct: createProduct.mutateAsync,
    deleteSubcategory: deleteSubcategory.mutateAsync, // ⚠️ ahora espera { id, categoryId }
    deleteProduct: deleteProduct.mutateAsync,         // ⚠️ ahora espera { id, subcategoryId }
    createVideo: createVideo.mutateAsync,
    updateVideo: updateVideo.mutateAsync,
    deleteVideo: deleteVideo.mutateAsync,

    // Estados de mutations
    isCreatingSubcategory: createSubcategory.isPending,
    isCreatingProduct: createProduct.isPending,
    isDeletingSubcategory: deleteSubcategory.isPending,
    isDeletingProduct: deleteProduct.isPending,
    isCreatingVideo: createVideo.isPending,
    isUpdatingVideo: updateVideo.isPending,
    isDeletingVideo: deleteVideo.isPending,

    updateProduct: updateProduct.mutateAsync,
    isUpdatingProduct: updateProduct.isPending,

    // Utilidades
    getCategoryStats,
    refreshData: () => {
       // ✅ Refetch contextual: evita refetchear todo a la vez
      if (activeTab === 'categories' || activeTab === 'subcategories') {
        categoriesQuery.refetch();
        subcategoriesQuery.refetch();
      } else if (activeTab === 'products') {
        // 👇 Recarga categorías también (porque de ahí salen los productos)
        categoriesQuery.refetch();
        productsQuery.refetch();
      } else if (activeTab === 'videos') {
        videosQuery.refetch();
      } else if (activeTab === 'tests') {
        testQuestionsQuery.refetch();
      }
    }
  };
};
